import { Stack, Typography } from '@mui/joy';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { useFormContext } from 'core/states';
import { PartNumberHelper } from 'parts/helpers';
import { QuotePartDetails } from 'quotes/components/QuotePartDetails';
import { useSelectedQuotePartContext } from 'quotes/states';
import { IQuotePart } from 'quotes/types';
import { Direction, Level, Variant } from 'theme/enums';

import { usePriceFormFieldsContext } from './state';

export const ViewPreviousPrices = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const { t } = useTranslation();

  const { watch, setValue } = useFormContext();

  const { selectedQuotePart } = useSelectedQuotePartContext();
  const { onChangePrice, quote } = usePriceFormFieldsContext();

  const partValue = watch('part');

  const initialQuotePart = useMemo(() => {
    if (
      !partValue?.number ||
      PartNumberHelper.areTheSame(selectedQuotePart?.partArr?.number, partValue?.number)
    ) {
      return selectedQuotePart;
    }

    return {
      alternateVendorPartNumber: partValue?.alternateVendorPartNumber,
      partArr: partValue,
    };
  }, [selectedQuotePart, partValue]);

  const onReusePrice = useCallback(
    (unitPrice: number) => {
      setValue('price', unitPrice, { shouldValidate: true });
      onChangePrice({ source: 'event', target: { value: unitPrice } });
      setIsDetailsOpen(false);
    },
    [setValue, onChangePrice]
  );

  if (!quote || (!partValue?.number && !selectedQuotePart?.partArr?.number)) {
    return null;
  }

  return (
    <>
      <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
        <Typography level={Level.TITLE_MD}>{t('labelOr')}</Typography>
        <Button
          text={t('buttonLabelViewPreviousPrices')}
          variant={Variant.SOFT}
          onClick={() => setIsDetailsOpen(true)}
        />
      </Stack>

      <QuotePartDetails
        quote={quote}
        quotePart={initialQuotePart as Partial<IQuotePart>}
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        onReusePrice={onReusePrice}
      />
    </>
  );
};
